// Unified API - exposes window.api for Electron component compatibility

import * as productsApi from './fidelitySystem/products.api';
import * as brandsApi from './fidelitySystem/brands.api';
import * as productsScansApi from './fidelitySystem/productsScans.api';
import * as rewardsApi from './fidelitySystem/rewards.api';
import * as rewardsClaimsApi from './fidelitySystem/rewardsClaims.api';
import * as dataExportApi from './fidelitySystem/dataExport.api';
import * as influencersApi from './referralSystem/influencers.api';
import * as referralCodesApi from './referralSystem/referralCodes.api';
import * as savReportsApi from './savSystem/reports.api';
import * as savAgentsApi from './savSystem/agents.api';
import * as savTicketsApi from './savSystem/tickets.api';
import * as savStatisticsApi from './savSystem/statistics.api';
import { globalsApi } from './globals.api';
import { wsService } from '../services/websocket';

// Create window.ipcRenderer replacement for WebSocket
export const ipcRenderer = {
  /**
   * Subscribe to progress updates (replaces IPC receive)
   */
  receive: (channel: string, callback: (data: any) => void) => {
    wsService.subscribeToProgress(channel, callback);
  },

  /**
   * Unsubscribe from all listeners (replaces IPC removeAllListeners)
   */
  removeAllListeners: (channel: string) => {
    wsService.unsubscribeFromProgress(channel);
  },

  /**
   * Send event (if needed in the future)
   */
  send: (channel: string, data?: any) => {
    wsService.emit(channel, data);
  },
};

// Unified API object matching Electron's window.api
export const api = {
  // Products API
  fetchProducts: productsApi.fetchProducts,
  fetchPaginatedProducts: productsApi.fetchPaginatedProducts,
  getProductById: productsApi.getProductById,
  addProduct: productsApi.addProduct,
  updateProduct: productsApi.updateProduct,
  deleteProduct: productsApi.deleteProduct,
  canDeleteProduct: productsApi.canDeleteProduct,
  toggleArchiveProduct: productsApi.toggleArchiveProduct,
  importProductUnitsFile: productsApi.importProductUnitsFile,
  addProductUnits: productsApi.addProductUnits,
  fetchProductUnits: productsApi.fetchProductUnits,

  // Brands API
  fetchBrands: brandsApi.fetchBrands,
  fetchPaginatedBrands: brandsApi.fetchPaginatedBrands,
  getBrandById: brandsApi.getBrandById,
  addBrand: brandsApi.addBrand,
  updateBrand: brandsApi.updateBrand,
  deleteBrand: brandsApi.deleteBrand,
  canDeleteBrand: brandsApi.canDeleteBrand,
  toggleArchiveBrand: brandsApi.toggleArchiveBrand,

  // Products Scans API
  fetchPendingProductsScans: productsScansApi.fetchPendingProductsScans,
  fetchPaginatedProductsScans: productsScansApi.fetchPaginatedProductsScans,
  fetchPaginatedPendingProductsScans: productsScansApi.fetchPaginatedPendingProductsScans,
  getProductScanById: productsScansApi.getProductScanById,
  validateProductScan: productsScansApi.validateProductScan,
  rejectProductScan: productsScansApi.rejectProductScan,

  // Globals API
  setupGlobalData: globalsApi.setupGlobalData,
  fetchStatistics: globalsApi.fetchStatistics,
  fetchLocationsData: globalsApi.fetchLocationsData,
  getIntroductionVideoUrl: globalsApi.getIntroductionVideoUrl,
  setIntroductionVideoUrl: globalsApi.setIntroductionVideoUrl,
  getAgentsTelegramChannelUrl: globalsApi.getAgentsTelegramChannelUrl,
  setAgentsTelegramChannelUrl: globalsApi.setAgentsTelegramChannelUrl,
  getTermsAndConditions: globalsApi.getTermsAndConditions,
  setTermsAndConditions: globalsApi.setTermsAndConditions,
  getSavTermsAndConditions: globalsApi.getSavTermsAndConditions,
  setSavTermsAndConditions: globalsApi.setSavTermsAndConditions,
  getWhatsappFollowupConfig: globalsApi.getWhatsappFollowupConfig,
  setWhatsappFollowupConfig: globalsApi.setWhatsappFollowupConfig,

  // Rewards Claims API
  fetchPendingRewardClaims: rewardsClaimsApi.fetchPendingRewardClaims,
  fetchPaginatedRewardClaims: rewardsClaimsApi.fetchPaginatedRewardClaims,
  fetchPaginatedPendingRewardClaims: rewardsClaimsApi.fetchPaginatedPendingRewardClaims,
  validateRewardClaim: rewardsClaimsApi.validateRewardClaim,
  rejectRewardClaim: rewardsClaimsApi.rejectRewardClaim,

  // Influencers API
  fetchInfluencers: influencersApi.fetchInfluencers,
  fetchPaginatedInfluencers: influencersApi.fetchPaginatedInfluencers,
  getInfluencerById: influencersApi.getInfluencerById,
  addInfluencer: influencersApi.addInfluencer,
  updateInfluencer: influencersApi.updateInfluencer,
  deleteInfluencer: influencersApi.deleteInfluencer,
  canDeleteInfluencer: influencersApi.canDeleteInfluencer,
  payInfluencer: influencersApi.payInfluencer,
  hasReferralRequests: influencersApi.hasReferralRequests,
  validateAllReferralRequests: influencersApi.validateAllReferralRequests,

  // Rewards API
  fetchRewards: rewardsApi.fetchRewards,
  fetchPaginatedRewards: rewardsApi.fetchPaginatedRewards,
  addReward: rewardsApi.addReward,
  updateReward: rewardsApi.updateReward,
  deleteReward: rewardsApi.deleteReward,
  canDeleteReward: rewardsApi.canDeleteReward,
  toggleArchiveReward: rewardsApi.toggleArchiveReward,

  // Referral Codes API
  fetchReferralCodes: referralCodesApi.fetchReferralCodes,
  fetchPaginatedReferralCodes: referralCodesApi.fetchPaginatedReferralCodes,
  fetchReferralCodeByInfluencerId: referralCodesApi.fetchReferralCodeByInfluencerId,
  getReferralCodeById: referralCodesApi.getReferralCodeById,
  addReferralCode: referralCodesApi.addReferralCode,
  updateReferralCode: referralCodesApi.updateReferralCode,
  deleteReferralCode: referralCodesApi.deleteReferralCode,
  canDeleteReferralCode: referralCodesApi.canDeleteReferralCode,
  toggleActiveReferralCode: referralCodesApi.toggleActiveReferralCode,

  // Data Export API
  exportRewardsData: dataExportApi.exportRewardsData,
  exportProductData: dataExportApi.exportProductData,
  exportProductsScansData: dataExportApi.exportProductsScansData,
  exportRewardsClaimsData: dataExportApi.exportRewardsClaimsData,

  // SAV Reports API
  fetchPaginatedReports: savReportsApi.fetchPaginatedReports,
  fetchReportsPage: savReportsApi.fetchReportsPage,
  getReportById: savReportsApi.getReportById,
  seeReport: savReportsApi.seeReport,
  updateReport: savReportsApi.updateReport,
  deleteReport: savReportsApi.deleteReport,
  markReportAccounted: savReportsApi.markReportAccounted,
  sendReportFeedbackWhatsApp: savReportsApi.sendReportFeedbackWhatsApp,
  generateReport: savReportsApi.generateReport,
  exportReports: savReportsApi.exportReports,
  openReportFile: savReportsApi.openReportFile,
  openReportsFolder: savReportsApi.openReportsFolder,
  isReportGenerated: savReportsApi.isReportGenerated,
  fetchReportsMedia: savReportsApi.fetchReportsMedia,

  // SAV Agents API
  fetchPaginatedValidatedAgents: savAgentsApi.fetchPaginatedValidatedAgents,
  fetchPaginatedPendingAgents: savAgentsApi.fetchPaginatedPendingAgents,
  fetchPaginatedOnHoldAgents: savAgentsApi.fetchPaginatedOnHoldAgents,
  fetchPaginatedRejectedAgents: savAgentsApi.fetchPaginatedRejectedAgents,
  fetchPaginatedValidatedAndArchivedAgents: savAgentsApi.fetchPaginatedValidatedAndArchivedAgents,
  validateAgentIdentity: savAgentsApi.validateAgentIdentity,
  rejectAgentIdentity: savAgentsApi.rejectAgentIdentity,
  putAgentIdentityOnHold: savAgentsApi.putAgentIdentityOnHold,
  archiveValidatedAgent: savAgentsApi.archiveValidatedAgent,
  unArchiveValidatedAgent: savAgentsApi.unArchiveValidatedAgent,
  deleteUser: savAgentsApi.deleteUser,
  exportAgents: savAgentsApi.exportAgents,

  // SAV Tickets API
  fetchSavPaginatedTickets: savTicketsApi.fetchSavPaginatedTickets,

  // SAV Statistics API
  fetchSavStatistics: savStatisticsApi.fetchSavStatistics,
  exportSavStatistics: savStatisticsApi.exportSavStatistics,
};

// Export types for TypeScript support
export type VinkoAPI = typeof api;
export type VinkoIPCRenderer = typeof ipcRenderer;

// Declare global window interface
declare global {
  interface Window {
    api: VinkoAPI;
    ipcRenderer: VinkoIPCRenderer;
  }
}

// Expose on window for backward compatibility
if (typeof window !== 'undefined') {
  window.api = api;
  window.ipcRenderer = ipcRenderer;
}
